import React, { useCallback, useContext, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProductItemResponse } from '@/api/product/types';
import { ActionModal, AlertTypes, Button, Drawer, Icon, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { getListApplicationRegistrationsGroupedQueryKey } from '@/generated/application-registrations';
import {
	AccountId,
	FilterUnitRegistration,
	type FilterUnitRegistrationUpdateRequest,
	RegistrationCompositeId,
} from '@/generated/data.schemas';
import {
	getGetFilterRegistrationQueryKey,
	useDeleteFilterRegistration,
	useUpdateFilterRegistration,
} from '@/generated/filter-registration';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './FilterUnit.module.scss';
import { FilterUnitForm } from './shared/FilterUnitForm';

interface EditFilterUnitProps {
	ariaControlId: string;
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	filterUnit: FilterUnitRegistration;
	productItems?: ProductItemResponse[] | null;
}

export const EditFilterUnit: React.FunctionComponent<EditFilterUnitProps> = ({
	ariaControlId,
	accountId,
	registrationCompositeId,
	filterUnit,
	productItems,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const addNotification = useNotificationStore((state) => state.addNotification);
	const queryClient = useQueryClient();

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
	const [hasFormChanged, setHasFormChanged] = useState(false);

	const formId = 'edit-filerUnit-form'; // used for binding form and submit button
	const drawerRef = useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const updateFilterRegistrationMutation = useUpdateFilterRegistration({
		mutation: {
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});
				// refresh filter unit after update
				await queryClient.invalidateQueries({
					queryKey: getGetFilterRegistrationQueryKey(
						segmentationId,
						accountId,
						registrationCompositeId,
						filterUnit?.id,
					),
				});

				// refresh groups list to show filter unit name in list
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
				});

				addNotification({
					children: translations?.data.filterRegistrations.saveSuccess,
					type: AlertTypes.SUCCESS,
				});

				handleClose();
			},
			onError: (axiosError) => {
				const error = axiosError.response?.data;
				if (!error) {
					console.error('Unknown error', axiosError);
				} else if (isHttpValidationProblemDetails(error)) {
					addNotification({
						children: translations?.data.filterRegistrations.saveFailed,
						type: AlertTypes.DANGER,
					});

					setValidationErrors(error.errors);
				} else {
					addNotification({
						children: translations?.data.shared.genericErrorMsg,
						type: AlertTypes.DANGER,
					});
				}
			},
		},
	});

	const deleteFilterRegistrationMutation = useDeleteFilterRegistration({
		mutation: {
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});

				// refresh application registration grouped list query cache
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
				});

				closeModal();
				handleClose();

				addNotification({
					children: translations?.data.filterRegistrations.deleteFilterRegistrations.success,
					type: AlertTypes.SUCCESS,
				});
			},
			onError: () => {
				addNotification({
					children: translations?.data.filterRegistrations.deleteFilterRegistrations.failed,
					type: AlertTypes.DANGER,
				});
			},
		},
	});

	const openDrawer = useCallback((): void => {
		setHasFormChanged(false);
		handleOpen();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// set has input text changed state - used for disabling the save button
	const handleChange = (): void => {
		if (hasFormChanged) return;
		setHasFormChanged(true);
	};

	const openModal = useCallback(() => {
		setIsDeleteConfirmOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setIsDeleteConfirmOpen(false);
	}, []);

	const handleRemoveFilterUnit = () => {
		if (!filterUnit) return;

		deleteFilterRegistrationMutation.mutate({
			segmentationId,
			accountId,
			registrationCompositeId,
			filterUnitId: filterUnit.id,
		});
	};

	const handleSubmit = (payload: FilterUnitRegistrationUpdateRequest) => {
		setValidationErrors(null);

		updateFilterRegistrationMutation.mutate({
			segmentationId,
			accountId,
			registrationCompositeId,
			filterUnitId: filterUnit.id,
			data: payload,
		});
	};

	return (
		<>
			{/* Trigger button */}
			{filterUnit?.id ? (
				<Button
					aria-label={translations?.data.filterRegistrations.editFilterUnit}
					variant={'ghost'}
					onClick={openDrawer}
					aria-expanded={isOpen}
					aria-controls={ariaControlId}
				>
					<Icon name={'edit'} size={'sm'} />
				</Button>
			) : (
				<Button isFullWidth onClick={openDrawer} aria-expanded={isOpen} aria-controls={ariaControlId}>
					{translations?.data.filterRegistrations.addFilterUnit}
					<Icon name={'plus'} size={'sm'} />
				</Button>
			)}

			{isRendered && (
				<Drawer
					drawerRef={drawerRef}
					isOpen={isOpen}
					id={ariaControlId}
					desktopWidth={'400px'}
					direction={'Right'}
				>
					<Drawer.Header
						title={translations?.data.filterRegistrations.editFilterUnit}
						handleClose={handleClose}
					/>

					<Drawer.Body>
						<div className={styles.innerBody}>
							<FilterUnitForm
								formId={formId}
								legend={translations?.data.sparePartRegistrations.editSparePart}
								filterUnit={filterUnit}
								productItems={productItems}
								validationErrors={validationErrors}
								handleSubmit={handleSubmit}
								handleChange={handleChange}
								accountId={accountId}
							/>

							<Button onClick={openModal} variant={'outline'} isFullWidth>
								{translations?.data.shared.delete}
								<Icon name={'trash'} size={'md'} />
							</Button>

							{isDeleteConfirmOpen && (
								<ActionModal
									size={'sm'}
									translations={translations?.data}
									onSubmit={handleRemoveFilterUnit}
									onCancel={closeModal}
									headline={translations?.data.filterRegistrations.deleteFilterRegistrations.headline}
									message={translations?.data.filterRegistrations.deleteFilterRegistrations.teaser}
								/>
							)}
						</div>
					</Drawer.Body>

					<Drawer.Footer className={styles.footer} hasNoPadding>
						<Button variant={'ghost'} isFullWidth onClick={handleClose}>
							{translations?.data.shared.cancel}
						</Button>
						<Button type="submit" form={formId} variant={'primary'} isFullWidth disabled={!hasFormChanged}>
							{translations?.data.filterRegistrations.save}
						</Button>
					</Drawer.Footer>
				</Drawer>
			)}
		</>
	);
};
